import { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { Product } from "~data/Product";

import { ApplicationLayoutProps } from "../../../components/layouts/Application";
import { productName } from "../../../formatters/billing";
import t from "../../../i18n";
import { useAuthentication } from "../../AuthenticationProvider";
import { SidebarItem } from "./navigation";

export default function useTopBar({
  currentProduct,
  currentRoute,
}: {
  currentProduct: Product | null;
  currentRoute: SidebarItem["id"] | null;
}): ApplicationLayoutProps["topBar"] {
  const { user, teams, teamMemberships, impersonating, stopImpersonation } = useAuthentication();
  const { pathname } = useLocation();

  const purchaseRequiredTeam = useMemo(() => {
    return (
      teamMemberships
        ?.filter((m) => m.permissions.canPurchase)
        .map((m) => m.team)
        .filter((t) => !currentProduct || t.product === currentProduct)
        .find((t) => !t.isSubaccount && !t.paying && !t.trialEndsOn && !t.trialAvailable && !t.freeAccount) || null
    );
  }, [teamMemberships, currentProduct]);

  const bannedTeam = useMemo(() => {
    return teams?.find((t) => t.banned) || null;
  }, [teams]);

  const onTrialNoCardTeam = useMemo(() => {
    return (
      teamMemberships
        ?.filter((m) => m.permissions.canPurchase)
        .map((m) => m.team)
        .filter((t) => !currentProduct || t.product === currentProduct)
        .find((t) => t.trialEndsOn && !t.trialConvertsAutomatically) || null
    );
  }, [teamMemberships, currentProduct]);

  const delinquentTeam = useMemo(() => {
    return (
      teamMemberships
        ?.filter((m) => m.permissions.canManageBilling)
        .map((m) => m.team)
        .filter((t) => !currentProduct || t.product === currentProduct)
        .find((t) => !!t.delinquentSince) || null
    );
  }, [teamMemberships, currentProduct]);

  if (impersonating) {
    return {
      content: `You are currently impersonating ${user.name}!`,
      cta: { label: "Stop impersonating", onClick: stopImpersonation },
    };
  }

  if (bannedTeam) {
    return {
      content: t("Our team has been trying to contact you. Please get in touch to continue using Upscope."),
      cta: { label: t("Contact us"), to: "mailto:team@upscope.com" },
    };
  }

  if (purchaseRequiredTeam && currentRoute !== "up.getting-started") {
    return {
      content: t("Your free trial has ended. Please purchase a subscription to continue using %{product}", {
        product: productName(purchaseRequiredTeam.product),
      }),
      cta: {
        label: t("Purchase"),
        to: ["up.onboarding.purchase", { teamDomain: purchaseRequiredTeam.domain }],
      },
    };
  }

  if (delinquentTeam && currentRoute !== "up.billing" && !pathname.startsWith("/invoice/")) {
    return {
      content: t(
        "A recent payment attempt failed. Please make sure you complete the payment to continue using  %{product}.",
        {
          product: productName(delinquentTeam.product),
        },
      ),
      cta: {
        label: t("Complete payment"),
        to: ["up.settings.billing", { teamDomain: delinquentTeam.domain }],
      },
    };
  }

  if (onTrialNoCardTeam && currentRoute !== "up.getting-started") {
    return {
      content: t("Your free trial will end on %{formattedDate}. Add a card to continue using %{product}.", {
        product: productName(onTrialNoCardTeam.product),
        formattedDate: onTrialNoCardTeam.trialEndsOn!.toLocaleDateString(),
      }),
      cta: {
        label: t("Add payment method"),
        to: ["up.onboarding.purchase", { teamDomain: onTrialNoCardTeam.domain }],
      },
    };
  }
}
